/**
 * FRONTROOMS Sector Generator
 * Infinite sector chunk-based environment builder.
 * Procedurally structures 5 distinct zones: Lobby, Mall, Hotel, Airport, and Office.
 * Handles dynamic objects (elevators, flickering lights) and compiles physical AABBs.
 */

export class SectorGenerator {
  constructor(scene, textureGen, audioSynth) {
    this.scene = scene;
    this.textures = textureGen;
    this.audio = audioSynth;
    this.THREE = textureGen.THREE;

    // Sector config
    this.sectorSize = 20; // 20m x 20m chunks
    this.ceilingHeight = 3.80; // default height in meters
    this.activeSectors = new Map(); // gridKey -> sectorObject
    this.startGx = 0;
    this.startGz = 0;
    
    // Global lists for updater in world loop
    this.flickeringLights = []; // active flickering panels
    this.elevators = [];        // active elevators
    this.animatedChevrons = [];  // animated running floor chevrons
    this.arrowClock = 0;        // master clock for chevrons animation

    // Cached geometries to optimize performance
    this.geometries = {
      wall: new this.THREE.BoxGeometry(1, 1, 1),
      plane: new this.THREE.PlaneGeometry(1, 1),
      cylinder: new this.THREE.CylinderGeometry(1, 1, 1, 8),
      sphere: new this.THREE.SphereGeometry(1, 8, 8)
    };
  }

  /**
   * Procedurally determines solid walls and doorways using only the shared coordinates at the boundaries.
   * This guarantees adjacent sectors will always agree on their common boundaries.
   */
  isSolidBoundary(gx, gz, type) {
    // Highway corridor overrides: vertical highways run North-South, horizontal highways run East-West
    if (type === 'horizontal') {
      if (gx % 4 === 0) return false; // horizontal highway is always open
    } else {
      if (gz % 3 === 0) return false; // vertical highway is always open
    }
    
    // Deterministic hash of coordinate
    const hash = Math.abs(this.hash2D(gx, gz));
    // Balanced 40% chance of a solid wall, otherwise open doorway passage
    return (hash % 5) < 2;
  }

  /**
   * Deterministic hash function for coordinates.
   */
  hash2D(x, z) {
    let h1 = x * 374761393;
    let h2 = z * 668265263;
    let n = h1 + h2;
    n = (n ^ (n >> 13)) * 1274126177;
    return n ^ (n >> 16);
  }

  /**
   * Streams sectors around the player grid index.
   * @param {number} pgx - Player grid X coordinate
   * @param {number} pgz - Player grid Z coordinate
   * @param {number} radius - Loading radius (sectors)
   * @returns {Array} List of active physical bounding boxes (AABBs)
   */
  updateSectors(pgx, pgz, radius = 2) {
    const keepKeys = new Set();
    const collisionBoxes = [];

    // 1. Spawns sectors within the grid radius
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dz = -radius; dz <= radius; dz++) {
        const gx = pgx + dx;
        const gz = pgz + dz;
        const key = `${gx},${gz}`;
        keepKeys.add(key);

        if (!this.activeSectors.has(key)) {
          this.buildSector(gx, gz, key);
        }

        // Gather collision boxes from this sector
        const sector = this.activeSectors.get(key);
        if (sector && sector.collisionBoxes) {
          collisionBoxes.push(...sector.collisionBoxes);
        }
      }
    }

    // 2. Unloads distant sectors to preserve memory and FPS
    for (const [key, sector] of this.activeSectors.entries()) {
      if (!keepKeys.has(key)) {
        this.unloadSector(key, sector);
      }
    }

    return collisionBoxes;
  }

  /**
   * Unloads sector meshes from Three.js scene and cleans references.
   */
  unloadSector(key, sector) {
    // Remove group from scene
    this.scene.remove(sector.group);

    // Dispose geometries and materials inside group meshes
    sector.group.traverse((node) => {
      if (node.isMesh) {
        node.geometry.dispose();
        // Skip materials if they are shared/cached
      }
    });

    // Remove lights, elevators, and chevrons from tracking
    this.flickeringLights = this.flickeringLights.filter(l => l.sectorKey !== key);
    this.elevators = this.elevators.filter(e => e.sectorKey !== key);
    this.animatedChevrons = this.animatedChevrons.filter(c => c.sectorKey !== key);

    this.activeSectors.delete(key);
  }

  /**
   * Builds the geometry and meshes for a specific grid coordinates.
   */
  buildSector(gx, gz, key) {
    const group = new this.THREE.Group();
    // Center position of this chunk
    const cx = gx * this.sectorSize;
    const cz = gz * this.sectorSize;
    group.position.set(cx, 0, cz);

    const hashVal = Math.abs(this.hash2D(gx, gz));
    const zoneType = hashVal % 10; // 10 unique liminal styles

    // Setup local sector data container
    const sectorData = {
      group: group,
      collisionBoxes: [],
      gx: gx,
      gz: gz
    };

    // Determine walls layout mathematically consistent at shared boundaries
    const finalN = this.isSolidBoundary(gx, gz, 'horizontal');
    const finalS = this.isSolidBoundary(gx, gz + 1, 'horizontal');
    const finalE = this.isSolidBoundary(gx + 1, gz, 'vertical');
    const finalW = this.isSolidBoundary(gx, gz, 'vertical');

    // Build the specific zone style
    switch (zoneType) {
      case 0:
        this.buildLobbySector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 1:
        this.buildMallSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 2:
        this.buildHotelSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 3:
        this.buildAirportSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 4:
        this.buildCorporateSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 5:
        this.buildLibrarySector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 6:
        this.buildDataSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 7:
        this.buildPoolSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 8:
        this.buildGreenhouseSector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
      case 9:
        this.buildGallerySector(group, cx, cz, finalN, finalS, finalE, finalW, sectorData);
        break;
    }

    // Add elevator occasionally (10% chance in lobbies or office corridors, bypass start sector)
    const isStartSector = (gx === this.startGx && gz === this.startGz);
    if ((zoneType === 0 || zoneType === 4) && (hashVal % 10 === 0) && !isStartSector) {
      this.placeElevator(group, cx, cz, key, sectorData);
    }

    // Add glowing suspended signpost directory in the center of all rooms pointing to an open neighbor sector
    const sectorH = (zoneType === 3 || zoneType === 7 || zoneType === 8) ? 7.5 : this.ceilingHeight;
    this.addSectorCeilingSignpost(group, sectorH, !finalN, !finalS, !finalE, !finalW, gx, gz, zoneType);

    this.scene.add(group);
    this.activeSectors.set(key, sectorData);
  }

  /**
   * Builds and suspends a beautiful glowing N64 directory sign from the ceiling,
   * showing an arrow pointing to the open generated pathway and displaying the room name.
   */
  addSectorCeilingSignpost(group, h, openN, openS, openE, openW, gx, gz, zoneType) {
    // 1. Determine which direction is open and leads to a neighbor
    let targetGx = gx;
    let targetGz = gz;
    let arrowChar = "▲";
    
    if (openN) {
      targetGz = gz - 1;
      arrowChar = "▲";
    } else if (openS) {
      targetGz = gz + 1;
      arrowChar = "▼";
    } else if (openE) {
      targetGx = gx + 1;
      arrowChar = "➔";
    } else if (openW) {
      targetGx = gx - 1;
      arrowChar = "◀";
    } else {
      // If fully closed (safety default), point North
      targetGz = gz - 1;
      arrowChar = "▲";
    }

    // 2. Resolve neighbor room type and name
    const neighborHash = Math.abs(this.hash2D(targetGx, targetGz));
    const neighborZoneType = neighborHash % 10;
    
    let neighborName = "LOBBY INFINITO";
    let glowColor = "#ffcf40";
    if (neighborZoneType === 1) {
      neighborName = "SHOPPING SILENCIOSO";
      glowColor = "#39ff14";
    } else if (neighborZoneType === 2) {
      neighborName = "HOTEL IMPOSIBLE";
      glowColor = "#ff3b30";
    } else if (neighborZoneType === 3) {
      neighborName = "AEROPUERTO DORMIDO";
      glowColor = "#00bcd4";
    } else if (neighborZoneType === 4) {
      neighborName = "CENTRO CORPORATIVO";
      glowColor = "#9c27b0";
    } else if (neighborZoneType === 5) {
      neighborName = "BIBLIOTECA INTERMINABLE";
      glowColor = "#ff8000";
    } else if (neighborZoneType === 6) {
      neighborName = "CENTRO DE DATOS";
      glowColor = "#007aff";
    } else if (neighborZoneType === 7) {
      neighborName = "PISCINA SUBTERRÁNEA";
      glowColor = "#00ffff";
    } else if (neighborZoneType === 8) {
      neighborName = "INVERNADERO VACÍO";
      glowColor = "#4cd964";
    } else if (neighborZoneType === 9) {
      neighborName = "GALERÍA DE ARTE";
      glowColor = "#ffffff";
    }

    // 3. Retrieve Signpost Material and solid grey metal material
    const translatedNeighborName = window.translate ? window.translate(neighborName) : neighborName;
    const signMat = this.textures.getCeilingSignpostMaterial(arrowChar, translatedNeighborName, glowColor);
    const metalMat = this.textures.getMetalMaterial();

    // 4. Build 3D Signboard model (width: 2.2m, height: 0.55m, depth: 0.16m)
    const signpostGroup = new this.THREE.Group();
    
    // Dynamically adjust cables & heights based on tall ceilings vs standard ceilings to prevent player camera overlap!
    const isTall = h > 4.0;
    const cableLength = isTall ? 1.5 : 0.5;
    const suspendOffset = isTall ? 1.8 : 0.75;
    
    // Hang it in the center of the sector chunk, suspended deterministically
    signpostGroup.position.set(0, h - suspendOffset, 0);

    const board = new this.THREE.Mesh(new this.THREE.BoxGeometry(2.2, 0.55, 0.16), signMat);
    signpostGroup.add(board);

    // Suspended cables
    const cableGeo = new this.THREE.CylinderGeometry(0.015, 0.015, cableLength, 6);
    
    const cableL = new this.THREE.Mesh(cableGeo, metalMat);
    cableL.position.set(-0.8, 0.55/2 + cableLength/2, 0);
    
    const cableR = new this.THREE.Mesh(cableGeo, metalMat);
    cableR.position.set(0.8, 0.55/2 + cableLength/2, 0);

    signpostGroup.add(cableL);
    signpostGroup.add(cableR);

    group.add(signpostGroup);
  }

  /* --- ZONES BUILDERS --- */

  /**
   * ZONE 0: Lobby Infinito (Beige/Yellow Carpet, columns, desks)
   */
  buildLobbySector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    // Materials
    const matFloor = this.textures.getCarpetMaterial('#cfbd97'); // warm tan carpet
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#ece7d5', '#ded9c9'); // beige striped wallpaper

    // Floor & Ceiling planes
    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 0);

    // Structural office columns in 4 corners
    const colGeo = new this.THREE.BoxGeometry(1.2, h, 1.2);
    const colBaseGeo = new this.THREE.BoxGeometry(1.26, 0.15, 1.26); // slightly wider baseboard
    const colCrownGeo = new this.THREE.BoxGeometry(1.26, 0.12, 1.26); // slightly wider crown trim
    const trimMat = this.textures.getMetalMaterial(); // sleek metal trim

    // Retro N64 Stone Bricks Column material with crisp non-stretched UV alignment
    const colTex = this.textures.loadRetroTexture('Stone bricks.png', 2.0, 6.0);
    const matColumn = new this.THREE.MeshStandardMaterial({
      map: colTex,
      roughness: 0.6,
      metalness: 0.1
    });

    const cols = [
      { x: -5, z: -5 }, { x: 5, z: -5 },
      { x: -5, z: 5 }, { x: 5, z: 5 }
    ];
    cols.forEach(c => {
      const colMesh = new this.THREE.Mesh(colGeo, matColumn);
      colMesh.position.set(c.x, h/2, c.z);
      colMesh.castShadow = true;
      colMesh.receiveShadow = true;
      group.add(colMesh);

      // Skirting board (Bottom)
      const colBase = new this.THREE.Mesh(colBaseGeo, trimMat);
      colBase.position.set(c.x, 0.075, c.z);
      group.add(colBase);

      // Crown molding (Top)
      const colCrown = new this.THREE.Mesh(colCrownGeo, trimMat);
      colCrown.position.set(c.x, h - 0.06, c.z);
      group.add(colCrown);

      this.addAABB(sData, cx + c.x, cz + c.z, 1.2, 1.2);
    });

    // Build Perimeter Walls
    this.buildChamberWalls(group, cx, cz, h, matWall, !wallN, !wallS, !wallE, !wallW, sData, 0);

    // Decorative Plants & Empty Counters
    // Potted plant (Do not spawn on starting sector center to prevent player spawn collision!)
    const isStart = (sData.gx === this.startGx && sData.gz === this.startGz);
    if (!isStart) {
      const plantGroup = this.createPottedPlant();
      plantGroup.position.set(0, 0, 0);
      group.add(plantGroup);
      this.addAABB(sData, cx, cz, 1.0, 1.0);
    }

    // Lighting panels setup
    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /**
   * ZONE 1: Shopping Silencioso (Polished floors, glass windows, empty stores)
   */
  buildMallSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    const matFloor = this.textures.getTerrazzoMaterial('#dfded9'); // light marble terrazzo
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#c2c3c7', '#b3b4b8'); // clean white/grey wall
    
    // Glass display panel material
    const matGlass = new this.THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.15,
      roughness: 0.05,
      metalness: 0.1,
      transmission: 0.9, // high-end transmission physics
      ior: 1.5,
      thickness: 0.1
    });

    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 1);

    // Instead of complete walls, some perimeter walls have glass display openings (empty stores)
    this.buildChamberWalls(group, cx, cz, h, matWall, !wallN, !wallS, !wallE, !wallW, sData, 1);

    // Ordered product displays (aesthetic columns of perfect cubes)
    if (Math.abs(this.hash2D(cx, cz)) % 2 === 0) {
      const displayGeo = new this.THREE.BoxGeometry(0.8, 1.6, 0.8);
      const displayMat = new this.THREE.MeshStandardMaterial({
        color: 0xfffdfa,
        roughness: 0.1,
        metalness: 0.9
      });
      const dispMesh = new this.THREE.Mesh(displayGeo, displayMat);
      dispMesh.position.set(2, 0.8, -2);
      group.add(dispMesh);
      this.addAABB(sData, cx + 2, cz - 2, 0.8, 0.8);
    }

    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /**
   * ZONE 2: Hotel Imposible (Vibrant red pattern carpet, long halls, doors)
   */
  buildHotelSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    const matFloor = this.textures.getCarpetMaterial('#6e1c24'); // deep red hotel carpet
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#ded9cc', '#cfc9ba'); // cream vintage wallpaper
    const matDoor = this.textures.getMetalMaterial();

    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 2);

    // Build Hotel corridors layout (narrower pathways, corridors restricted to center)
    // We add walls defining 3m corridors, creating deep long corridors
    this.buildCorridorWalls(group, cx, cz, h, matWall, matDoor, sData);
    this.buildChamberWalls(group, cx, cz, h, matWall, true, true, true, true, sData, 2);

    // Grid lighting
    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /**
   * ZONE 3: Aeropuerto Dormido (Tall atriums, metal columns, row of seats)
   */
  buildAirportSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const tallH = 7.5; // Double height for terminal feeling

    const matFloor = this.textures.getTerrazzoMaterial('#e5e5e7'); // bright glossy flooring
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#f5f5f7', '#ececed'); // glossy white aluminum cladding

    // Floor and high ceiling
    this.addFloorAndCeiling(group, size, tallH, matFloor, matCeiling, sData.gx, sData.gz, 3);

    // Huge steel pillars supporting the tall roof
    const metalMat = this.textures.getMetalMaterial();
    const pillarGeo = new this.THREE.CylinderGeometry(0.5, 0.5, tallH, 12);
    const pillarBaseGeo = new this.THREE.CylinderGeometry(0.54, 0.54, 0.22, 12); // circular baseboard
    const pillarCrownGeo = new this.THREE.CylinderGeometry(0.54, 0.54, 0.16, 12); // circular crown ring
    
    // Cylindrical Stone Bricks pillar material with exact UV wrapping calculations
    const colTexCyl = this.textures.loadRetroTexture('Stone bricks.png', 4.0, 12.0);
    const matPillar = new this.THREE.MeshStandardMaterial({
      map: colTexCyl,
      roughness: 0.6,
      metalness: 0.1
    });

    const pillars = [{ x: -6, z: -6 }, { x: 6, z: 6 }];
    pillars.forEach(p => {
      const pil = new this.THREE.Mesh(pillarGeo, matPillar);
      pil.position.set(p.x, tallH/2, p.z);
      group.add(pil);

      // Base cylinder ring
      const pilBase = new this.THREE.Mesh(pillarBaseGeo, metalMat);
      pilBase.position.set(p.x, 0.11, p.z);
      group.add(pilBase);

      // Crown cylinder ring
      const pilCrown = new this.THREE.Mesh(pillarCrownGeo, metalMat);
      pilCrown.position.set(p.x, tallH - 0.08, p.z);
      group.add(pilCrown);

      this.addAABB(sData, cx + p.x, cz + p.z, 1.0, 1.0);
    });

    this.buildChamberWalls(group, cx, cz, tallH, matWall, !wallN, !wallS, !wallE, !wallW, sData, 3);

    // Hang airport flight display sign
    const signMat = this.textures.getSignageMaterial('directory');
    const boardGeo = new this.THREE.BoxGeometry(2.5, 1.2, 0.2);
    const board = new this.THREE.Mesh(boardGeo, signMat);
    board.position.set(0, tallH - 1.8, 0);
    
    // Hanging cables
    const cableGeo = new this.THREE.CylinderGeometry(0.02, 0.02, 1.8, 4);
    const cableL = new this.THREE.Mesh(cableGeo, metalMat);
    cableL.position.set(-1, tallH - 0.9, 0);
    const cableR = new this.THREE.Mesh(cableGeo, metalMat);
    cableR.position.set(1, tallH - 0.9, 0);
    
    group.add(board);
    group.add(cableL);
    group.add(cableR);

    // Waiting benches grid layout (2 columns x 4 rows = 8 benches)
    const xOffsets = [-4.0, 4.0];
    const zOffsets = [-4.5, -1.5, 1.5, 4.5];

    xOffsets.forEach(bx => {
      zOffsets.forEach(bz => {
        const benchGroup = this.createAirportBenches();
        benchGroup.position.set(bx, 0, bz);
        group.add(benchGroup);
        
        // Register precise AABB physical boundaries to block player clipping
        this.addAABB(sData, cx + bx, cz + bz, 3.5, 1.0);
      });
    });

    // Retro Airport Plane Ticket shop ("tienda de tickets de avion") on the West side, South-West quadrant
    const ticketShop = this.createTicketShop();
    ticketShop.position.set(-7.6, 0, 3.0);
    ticketShop.rotation.y = Math.PI / 2; // Face East towards runway/benches
    group.add(ticketShop);
    // Block physical collision: counter desk is 4.5m along Z, 1.2m along X
    this.addAABB(sData, cx - 7.6, cz + 3.0, 1.2, 4.5);

    // Lights
    this.addGridCeilingLights(group, cx, cz, tallH, sData.gx, sData.gz);
  }

  /**
   * ZONE 4: Centro Corporativo (Grey tiles, cubicles, desks, machines)
   */
  buildCorporateSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    const matFloor = this.textures.getCarpetMaterial('#585e66'); // industrial grey office tiles
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#ecebeb', '#dedcdc');

    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 4);
    this.buildChamberWalls(group, cx, cz, h, matWall, !wallN, !wallS, !wallE, !wallW, sData, 4);

    // --- IMMERSIVE RETRO OFFICE CLUTTER (Dispersed and Piled PCs) ---
    
    // 1. Neat standing office cubicle at (3.5, 0, 3.5)
    const cubicle1 = this.createOfficeCubicle(matWall);
    cubicle1.position.set(3.5, 0, 3.5);
    group.add(cubicle1);
    this.addAABB(sData, cx + 3.5, cz + 3.5, 2.5, 2.5);

    // 2. Slightly crooked standing office cubicle at (-3.5, 0, -3.5)
    const cubicle2 = this.createOfficeCubicle(matWall);
    cubicle2.position.set(-3.5, 0, -3.5);
    cubicle2.rotation.y = Math.PI / 18; // slightly rotated to look abandoned
    group.add(cubicle2);
    this.addAABB(sData, cx - 3.5, cz - 3.5, 2.5, 2.5);

    // 3. Messy pile of stacked CRT computers and tipped desk at (-2.0, 0, 2.5)
    const pcPile = this.createCRTPile();
    pcPile.position.set(-2.0, 0, 2.5);
    pcPile.rotation.y = -Math.PI / 4;
    group.add(pcPile);
    this.addAABB(sData, cx - 2.0, cz + 2.5, 2.8, 2.8);

    // 4. A single abandoned tipped desk and CPU case at (4.0, 0, -4.0)
    const extraDeskMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Wood planks.png', 1, 1),
      color: new this.THREE.Color('#5a4230'),
      roughness: 0.6
    });
    const extraDeskTop = new this.THREE.Mesh(new this.THREE.BoxGeometry(2.2, 0.05, 0.8), extraDeskMat);
    extraDeskTop.position.set(4.0, 0.025, -4.0);
    extraDeskTop.rotation.y = Math.PI / 3;
    extraDeskTop.rotation.z = Math.PI / 2.2; // tilted on floor!
    group.add(extraDeskTop);
    
    const extraMonitor = this.createRetroPC();
    extraMonitor.position.set(4.3, 0.1, -3.6);
    extraMonitor.rotation.x = Math.PI / 3;
    extraMonitor.rotation.y = -Math.PI / 6;
    group.add(extraMonitor);
    
    this.addAABB(sData, cx + 4.0, cz - 4.0, 2.4, 2.0);

    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /* --- GEOMETRY UTILITIES & MODULAR BLOCKS --- */

  /**
   * Creates floor plane (with dynamic patterns and division strips) and ceiling plane.
   */
  addFloorAndCeiling(group, size, height, matFloor, matCeiling, gx = 0, gz = 0, zoneType = 0) {
    const trimMat = this.textures.getMetalMaterial();
    const hashVal = Math.abs(this.hash2D(gx, gz));
    const borderWidth = 0.8; // 80cm elegant border zócalo
    const centerSize = size - borderWidth * 2; // 18.4m center area

    // --- 1. FLOOR BORDER (ZÓCALO DE PISO PERIMETRAL) ---
    // Select border material depending on zoneType to create dynamic atmospheric separation
    let borderMat = trimMat; // default metallic
    if (zoneType === 0) {
      borderMat = this.textures.getWoodPlanksMaterial('#402517'); // dark mahogany wood planks
    } else if (zoneType === 1) {
      borderMat = this.textures.getTerrazzoMaterial('#1a1a1c'); // shiny black stone slate terrazzo
    } else if (zoneType === 2) {
      borderMat = this.textures.getCarpetMaterial('#3a0a0f'); // deep wine velvet hotel carpet
    } else if (zoneType === 3) {
      borderMat = this.textures.getStoneBricksMaterial('#44464a'); // slate bricks airport border
    } else if (zoneType === 4) {
      borderMat = this.textures.getCarpetMaterial('#2c2f33'); // dark slate felt corporate carpet
    }

    // Geometries for perimeter floor borders
    const borderGeoH = new this.THREE.PlaneGeometry(size, borderWidth);
    const borderGeoV = new this.THREE.PlaneGeometry(borderWidth, size - borderWidth * 2);

    // North border plane
    const bN = new this.THREE.Mesh(borderGeoH, borderMat);
    bN.rotation.x = -Math.PI / 2;
    bN.position.set(0, 0.002, -size/2 + borderWidth/2);
    bN.receiveShadow = true;
    group.add(bN);

    // South border plane
    const bS = new this.THREE.Mesh(borderGeoH, borderMat);
    bS.rotation.x = -Math.PI / 2;
    bS.position.set(0, 0.002, size/2 - borderWidth/2);
    bS.receiveShadow = true;
    group.add(bS);

    // East border plane
    const bE = new this.THREE.Mesh(borderGeoV, borderMat);
    bE.rotation.x = -Math.PI / 2;
    bE.position.set(size/2 - borderWidth/2, 0.002, 0);
    bE.receiveShadow = true;
    group.add(bE);

    // West border plane
    const bW = new this.THREE.Mesh(borderGeoV, borderMat);
    bW.rotation.x = -Math.PI / 2;
    bW.position.set(-size/2 + borderWidth/2, 0.002, 0);
    bW.receiveShadow = true;
    group.add(bW);

    // --- 2. INNER 3D BRASS/METAL DIVIDER FRAME (Zócalo Dividor Elevado) ---
    // A 3D trim profile separating the border from the center pattern area
    const dividerGeoH = new this.THREE.BoxGeometry(size, 0.015, 0.08); // raised 1.5cm, 8cm wide horizontal
    const dividerGeoV = new this.THREE.BoxGeometry(0.08, 0.015, size - borderWidth * 2); // raised 1.5cm, 8cm wide vertical
    const brassMat = new this.THREE.MeshStandardMaterial({
      color: 0xc5a059, // vintage gold brass
      roughness: 0.18,
      metalness: 0.9
    });
    // In mall / office we use chrome, hotel / lobby brass
    const frameMat = (zoneType === 0 || zoneType === 2) ? brassMat : trimMat;

    // Place the four internal dividers
    const divPos = size/2 - borderWidth; // 9.2m
    
    const divN = new this.THREE.Mesh(dividerGeoH, frameMat);
    divN.position.set(0, 0.0075, -divPos);
    group.add(divN);

    const divS = new this.THREE.Mesh(dividerGeoH, frameMat);
    divS.position.set(0, 0.0075, divPos);
    group.add(divS);

    const divE = new this.THREE.Mesh(dividerGeoV, frameMat);
    divE.position.set(divPos, 0.0075, 0);
    group.add(divE);

    const divW = new this.THREE.Mesh(dividerGeoV, frameMat);
    divW.position.set(-divPos, 0.0075, 0);
    group.add(divW);

    // --- 3. FLOOR CENTRAL DYNAMIC GEOMETRIC PATTERNS ---
    const buildPatterns = () => {
      // Pattern Selection based on coordinates deterministically
      const patternIdx = hashVal % 4;

      if (patternIdx === 0) {
        // --- PATTERN A: CHECKERBOARD SQUARES (Cuadrados) ---
        // Splitting the central 18.4m area into an 8x8 grid of alternating squares
        const gridCount = 8;
        const sqWidth = centerSize / gridCount; // 2.3m squares
        const sqGeo = new this.THREE.PlaneGeometry(sqWidth, sqWidth);

        // Select a contrasting secondary material
        let altMat = matFloor;
        if (zoneType === 0) {
          altMat = this.textures.getWoodPlanksMaterial('#6d452f'); // light mahogany wood planks
        } else if (zoneType === 1) {
          altMat = this.textures.getTerrazzoMaterial('#ffffff'); // pure white marble
        } else if (zoneType === 2) {
          altMat = this.textures.getCarpetMaterial('#941a24'); // bright crimson red
        } else if (zoneType === 3) {
          altMat = this.textures.getMetalMaterial(); // shiny metallic squares
        } else if (zoneType === 4) {
          altMat = this.textures.getCarpetMaterial('#40444b'); // lighter slate office tiles
        }

        const boardGroup = new this.THREE.Group();
        for (let row = 0; row < gridCount; row++) {
          for (let col = 0; col < gridCount; col++) {
            const isDark = (row + col) % 2 === 0;
            const sqMat = isDark ? altMat : matFloor;
            const sqMesh = new this.THREE.Mesh(sqGeo, sqMat);
            sqMesh.rotation.x = -Math.PI / 2;
            sqMesh.position.set(
              -centerSize/2 + sqWidth/2 + col * sqWidth,
              0.003,
              -centerSize/2 + sqWidth/2 + row * sqWidth
            );
            sqMesh.receiveShadow = true;
            boardGroup.add(sqMesh);
          }
        }
        group.add(boardGroup);
      }
      else if (patternIdx === 1) {
        // --- PATTERN B: DIRECTIONAL GUIDE CHEVRONS / ARROWS (Flechas) ---
        // Draw the base floor
        const baseGeo = new this.THREE.PlaneGeometry(centerSize, centerSize);
        const baseFloor = new this.THREE.Mesh(baseGeo, matFloor);
        baseFloor.rotation.x = -Math.PI / 2;
        baseFloor.position.y = 0.003;
        baseFloor.receiveShadow = true;
        group.add(baseFloor);

        // Add 3D glowing directional arrow tracks down the center corridors
        const baseArrowMat = new this.THREE.MeshStandardMaterial({
          map: this.textures.loadRetroTexture('Crystal 3.png', 1, 1),
          color: new this.THREE.Color(zoneType === 3 ? '#ffb900' : '#4bf542'), // neon orange for airport, neon green for others
          emissive: new this.THREE.Color(zoneType === 3 ? '#b86000' : '#106e00'),
          emissiveIntensity: 1.5,
          roughness: 0.15,
          metalness: 0.8
        });

        // 3D chevron arrow components - MUCH SMALLER and tighter
        const arrowStripGeo = new this.THREE.BoxGeometry(0.8, 0.01, 0.12);
        const sectorKey = `${gx},${gz}`;

        // Build chevrons along the Z center hallway axis and X center hallway axis
        const ZOffsets = [-7, -5, -3, -1, 1, 3, 5, 7];
        ZOffsets.forEach((offset, idx) => {
          // Clone material to animate chevrons individually
          const chevMatL = baseArrowMat.clone();
          const chevMatR = baseArrowMat.clone();

          const arrowGroupZ = new this.THREE.Group();
          arrowGroupZ.position.set(0, 0.004, offset);

          const stripZL = new this.THREE.Mesh(arrowStripGeo, chevMatL);
          stripZL.position.set(-0.25, 0, 0.12);
          stripZL.rotation.y = -Math.PI / 4;
          const stripZR = new this.THREE.Mesh(arrowStripGeo, chevMatR);
          stripZR.position.set(0.25, 0, 0.12);
          stripZR.rotation.y = Math.PI / 4;

          arrowGroupZ.add(stripZL);
          arrowGroupZ.add(stripZR);
          group.add(arrowGroupZ);

          // Track for animations
          this.animatedChevrons.push({
            mesh: stripZL,
            index: idx,
            sectorKey: sectorKey,
            baseEmissive: 1.5
          });
          this.animatedChevrons.push({
            mesh: stripZR,
            index: idx,
            sectorKey: sectorKey,
            baseEmissive: 1.5
          });

          // Add cross-corridor horizontal chevrons occasionally
          if (hashVal % 2 === 0) {
            const chevMatXL = baseArrowMat.clone();
            const chevMatXR = baseArrowMat.clone();

            const arrowGroupX = new this.THREE.Group();
            arrowGroupX.position.set(offset, 0.004, 0);
            arrowGroupX.rotation.y = Math.PI / 2; // turned 90 deg

            const stripXL = new this.THREE.Mesh(arrowStripGeo, chevMatXL);
            stripXL.position.set(-0.25, 0, 0.12);
            stripXL.rotation.y = -Math.PI / 4;
            const stripXR = new this.THREE.Mesh(arrowStripGeo, chevMatXR);
            stripXR.position.set(0.25, 0, 0.12);
            stripXR.rotation.y = Math.PI / 4;

            arrowGroupX.add(stripXL);
            arrowGroupX.add(stripXR);
            group.add(arrowGroupX);

            this.animatedChevrons.push({
              mesh: stripXL,
              index: idx,
              sectorKey: sectorKey,
              baseEmissive: 1.5
            });
            this.animatedChevrons.push({
              mesh: stripXR,
              index: idx,
              sectorKey: sectorKey,
              baseEmissive: 1.5
            });
          }
        });
      }
      else if (patternIdx === 2) {
        // --- PATTERN C: CENTRAL INSET PANEL WITH DIAMOND/CROSS PROFILE ---
        // A luxurious, slightly elevated central inset panel, with brass dividers
        const outerGeo = new this.THREE.PlaneGeometry(centerSize, centerSize);
        const outerBase = new this.THREE.Mesh(outerGeo, matFloor);
        outerBase.rotation.x = -Math.PI / 2;
        outerBase.position.y = 0.003;
        outerBase.receiveShadow = true;
        group.add(outerBase);

        // Central inset panel (e.g. 10m x 10m carpet or stone)
        const insetWidth = centerSize - 5.0; // 13.4m inset
        const insetGeo = new this.THREE.PlaneGeometry(insetWidth, insetWidth);
        
        let insetMat = matFloor;
        if (zoneType === 0) {
          insetMat = this.textures.getWoodPlanksMaterial('#7c4a34'); // wood planks center
        } else if (zoneType === 1) {
          insetMat = this.textures.getTerrazzoMaterial('#eaeaea'); // sleek white terrazzo
        } else if (zoneType === 2) {
          insetMat = this.textures.getCarpetMaterial('#940b15'); // luxury bright crimson
        } else if (zoneType === 3) {
          insetMat = this.textures.getStoneBricksMaterial('#8a8d94'); // stone bricks center
        } else if (zoneType === 4) {
          insetMat = this.textures.getCarpetMaterial('#33363b'); // dark office panel
        }

        const insetMesh = new this.THREE.Mesh(insetGeo, insetMat);
        insetMesh.rotation.x = -Math.PI / 2;
        insetMesh.position.set(0, 0.004, 0);
        insetMesh.receiveShadow = true;
        group.add(insetMesh);

        // Framed diamond cross divider strips inside the inset
        const profileGeo = new this.THREE.BoxGeometry(insetWidth * 1.414, 0.012, 0.06); // diagonal strip
        const diag1 = new this.THREE.Mesh(profileGeo, frameMat);
        diag1.position.set(0, 0.005, 0);
        diag1.rotation.y = Math.PI / 4; // 45 deg diagonal
        
        const diag2 = new this.THREE.Mesh(profileGeo, frameMat);
        diag2.position.set(0, 0.005, 0);
        diag2.rotation.y = -Math.PI / 4; // -45 deg diagonal

        group.add(diag1);
        group.add(diag2);
      }
      else {
        // --- PATTERN D: STRIPED PANEL GRID (Rayas) ---
        // A structured pattern formed by linear metal divisions splitting the floor into 4 long rectangles
        const outerGeo = new this.THREE.PlaneGeometry(centerSize, centerSize);
        const outerBase = new this.THREE.Mesh(outerGeo, matFloor);
        outerBase.rotation.x = -Math.PI / 2;
        outerBase.position.y = 0.003;
        outerBase.receiveShadow = true;
        group.add(outerBase);

        // Draw 3 thin metal stripes along the Z axis dividing the room floor into 4 sections
        const stripeGeo = new this.THREE.BoxGeometry(0.06, 0.012, centerSize);
        const stripOffsets = [-centerSize/4, 0, centerSize/4];
        
        stripOffsets.forEach(xOffset => {
          const strip = new this.THREE.Mesh(stripeGeo, frameMat);
          strip.position.set(xOffset, 0.004, 0);
          group.add(strip);
        });

        // Alternating color stripes occasionally
        if (hashVal % 2 === 0) {
          const alternateStripeGeo = new this.THREE.PlaneGeometry(centerSize/4 - 0.03, centerSize);
          let stripedMat = this.textures.getMetalMaterial();
          if (zoneType === 0) stripedMat = this.textures.getWoodPlanksMaterial('#503322');
          else if (zoneType === 2) stripedMat = this.textures.getCarpetMaterial('#5e131b');
          else if (zoneType === 4) stripedMat = this.textures.getCarpetMaterial('#2c2f33');

          const stripeMeshL = new this.THREE.Mesh(alternateStripeGeo, stripedMat);
          stripeMeshL.rotation.x = -Math.PI / 2;
          stripeMeshL.position.set(-centerSize * 3/8, 0.0035, 0);
          stripeMeshL.receiveShadow = true;

          const stripeMeshR = new this.THREE.Mesh(alternateStripeGeo, stripedMat);
          stripeMeshR.rotation.x = -Math.PI / 2;
          stripeMeshR.position.set(centerSize * 1/8, 0.0035, 0);
          stripeMeshR.receiveShadow = true;

          group.add(stripeMeshL);
          group.add(stripeMeshR);
        }
      }
    };

    buildPatterns();

    // --- 2. CEILING PLANE ---
    const ceilingGeo = new this.THREE.PlaneGeometry(size, size);
    const ceiling = new this.THREE.Mesh(ceilingGeo, matCeiling);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = height;
    ceiling.receiveShadow = true;
    group.add(ceiling);

    // --- 3. FLOOR BOUNDARY TRANSITION TRIM STRIPS (Zócalos de Piso) ---
    // Thin metal divider panels raised 1cm above floor at 4 edges of the sector grid
    const trimBorderH = new this.THREE.BoxGeometry(size, 0.01, 0.16); // horizontal divider
    const trimBorderV = new this.THREE.BoxGeometry(0.16, 0.01, size); // vertical divider

    // North transition edge
    const borderN = new this.THREE.Mesh(trimBorderH, trimMat);
    borderN.position.set(0, 0.005, -size/2);
    group.add(borderN);

    // South transition edge
    const borderS = new this.THREE.Mesh(trimBorderH, trimMat);
    borderS.position.set(0, 0.005, size/2);
    group.add(borderS);

    // East transition edge
    const borderE = new this.THREE.Mesh(trimBorderV, trimMat);
    borderE.position.set(size/2, 0.005, 0);
    group.add(borderE);

    // West transition edge
    const borderW = new this.THREE.Mesh(trimBorderV, trimMat);
    borderW.position.set(-size/2, 0.005, 0);
    group.add(borderW);
  }

  /**
   * Generates a fully enclosed room structure where open doorways connect chambers deterministically.
   * Spawns physical left/right panels with exact collision AABBs, a header wall panel,
   * and mounts a beautiful, glowing room name label sign above the doorway.
   */
  buildChamberWalls(group, cx, cz, h, matWall, openN, openS, openE, openW, sData, zoneType) {
    const size = this.sectorSize;
    const wThick = 0.5;
    const trimMat = this.textures.getMetalMaterial();

    // Setup Zone Label Sign details
    let zoneName = "LOBBY INFINITO";
    let glowColor = "#ffcf40";
    if (zoneType === 1) {
      zoneName = "SHOPPING SILENCIOSO";
      glowColor = "#39ff14";
    } else if (zoneType === 2) {
      zoneName = "HOTEL IMPOSIBLE";
      glowColor = "#ff3b30";
    } else if (zoneType === 3) {
      zoneName = "AEROPUERTO DORMIDO";
      glowColor = "#00bcd4";
    } else if (zoneType === 4) {
      zoneName = "CENTRO CORPORATIVO";
      glowColor = "#9c27b0";
    } else if (zoneType === 5) {
      zoneName = "BIBLIOTECA INTERMINABLE";
      glowColor = "#ff8000";
    } else if (zoneType === 6) {
      zoneName = "CENTRO DE DATOS";
      glowColor = "#007aff";
    } else if (zoneType === 7) {
      zoneName = "PISCINA SUBTERRÁNEA";
      glowColor = "#00ffff";
    } else if (zoneType === 8) {
      zoneName = "INVERNADERO VACÍO";
      glowColor = "#4cd964";
    } else if (zoneType === 9) {
      zoneName = "GALERÍA DE ARTE";
      glowColor = "#ffffff";
    }

    const translatedZoneName = window.translate ? window.translate(zoneName) : zoneName;
    const signMat = this.textures.getDoorwayLabelMaterial(translatedZoneName, glowColor);

    // Door Frame casing material: always solid grey metal trim
    const frameCasingMat = trimMat;

    // Wall geometries
    const solidWallGeoZ = new this.THREE.BoxGeometry(size, h, wThick);
    const solidWallGeoX = new this.THREE.BoxGeometry(wThick, h, size);

    // Doorway split wall geometries
    // Left/Right parts: 8.5m wide, full height
    const splitWallGeo = new this.THREE.BoxGeometry(8.5, h, wThick);
    const splitWallGeoX = new this.THREE.BoxGeometry(wThick, h, 8.5);

    // Header part above doorway: 3m wide, height h - 2.25 (leaving 2.25m bottom clearance, covered by casing)
    const headerHeight = h - 2.25;
    const headerGeoZ = new this.THREE.BoxGeometry(3.0, headerHeight, wThick);
    const headerGeoX = new this.THREE.BoxGeometry(wThick, headerHeight, 3.0);

    // Skirting (Bottom) and Crown (Top) trims
    const baseZGeoSolid = new this.THREE.BoxGeometry(size, 0.15, wThick + 0.04);
    const crownZGeoSolid = new this.THREE.BoxGeometry(size, 0.12, wThick + 0.04);
    const baseXGeoSolid = new this.THREE.BoxGeometry(wThick + 0.04, 0.15, size);
    const crownXGeoSolid = new this.THREE.BoxGeometry(wThick + 0.04, 0.12, size);

    const baseZGeoSplit = new this.THREE.BoxGeometry(8.5, 0.15, wThick + 0.04);
    const crownZGeoSplit = new this.THREE.BoxGeometry(8.5, 0.12, wThick + 0.04);
    const baseXGeoSplit = new this.THREE.BoxGeometry(wThick + 0.04, 0.15, 8.5);
    const crownXGeoSplit = new this.THREE.BoxGeometry(wThick + 0.04, 0.12, 8.5);

    // Glowing Sign Board Geometry
    const signGeoZ = new this.THREE.BoxGeometry(1.8, 0.45, 0.18);
    const signGeoX = new this.THREE.BoxGeometry(0.18, 0.45, 1.8);

    // Door Frame Geometries (Enlarged to completely cover boundaries and prevent Z-fighting)
    const frameJambGeoZ = new this.THREE.BoxGeometry(0.16, 2.2, wThick + 0.16);
    const frameHeaderGeoZ = new this.THREE.BoxGeometry(3.16, 0.16, wThick + 0.16);

    const frameJambGeoX = new this.THREE.BoxGeometry(wThick + 0.16, 2.2, 0.16);
    const frameHeaderGeoX = new this.THREE.BoxGeometry(wThick + 0.16, 0.16, 3.16);

    // --- 1. RENDER NORTH WALL (Shared boundary at Z = -10 with neighbor gz - 1) ---
    if (openN) {
      // Left Wall Partition (x = -5.75)
      const wLeft = new this.THREE.Mesh(splitWallGeo, matWall);
      wLeft.position.set(-5.75, h/2, -size/2);
      group.add(wLeft);

      const baseLeft = new this.THREE.Mesh(baseZGeoSplit, trimMat);
      baseLeft.position.set(-5.75, 0.075, -size/2);
      group.add(baseLeft);

      const crownLeft = new this.THREE.Mesh(crownZGeoSplit, trimMat);
      crownLeft.position.set(-5.75, h - 0.06, -size/2);
      group.add(crownLeft);

      this.addAABB(sData, cx - 5.75, cz - size/2, 8.5, wThick);

      // Right Wall Partition (x = 5.75)
      const wRight = new this.THREE.Mesh(splitWallGeo, matWall);
      wRight.position.set(5.75, h/2, -size/2);
      group.add(wRight);

      const baseRight = new this.THREE.Mesh(baseZGeoSplit, trimMat);
      baseRight.position.set(5.75, 0.075, -size/2);
      group.add(baseRight);

      const crownRight = new this.THREE.Mesh(crownZGeoSplit, trimMat);
      crownRight.position.set(5.75, h - 0.06, -size/2);
      group.add(crownRight);

      this.addAABB(sData, cx + 5.75, cz - size/2, 8.5, wThick);

      // Header Wall Panel (center, x = 0)
      const wHeader = new this.THREE.Mesh(headerGeoZ, matWall);
      wHeader.position.set(0, h - headerHeight/2, -size/2);
      group.add(wHeader);

      const crownHeader = new this.THREE.Mesh(new this.THREE.BoxGeometry(3.0, 0.12, wThick + 0.04), trimMat);
      crownHeader.position.set(0, h - 0.06, -size/2);
      group.add(crownHeader);

      // Door Frame Casing (Solid Grey Metal marcos only, NO swung-open door panels!)
      const jambL = new this.THREE.Mesh(frameJambGeoZ, frameCasingMat);
      jambL.position.set(-1.5, 1.1, -size/2);
      group.add(jambL);

      const jambR = new this.THREE.Mesh(frameJambGeoZ, frameCasingMat);
      jambR.position.set(1.5, 1.1, -size/2);
      group.add(jambR);

      const headerCasing = new this.THREE.Mesh(frameHeaderGeoZ, frameCasingMat);
      headerCasing.position.set(0, 2.28, -size/2);
      group.add(headerCasing);

      // Mounting Glowing Sign Board
      const signBoard = new this.THREE.Mesh(signGeoZ, signMat);
      signBoard.position.set(0, 2.45, -size/2 + 0.1);
      group.add(signBoard);
    } else {
      // Solid North Wall
      const w = new this.THREE.Mesh(solidWallGeoZ, matWall);
      w.position.set(0, h/2, -size/2);
      group.add(w);

      const wBase = new this.THREE.Mesh(baseZGeoSolid, trimMat);
      wBase.position.set(0, 0.075, -size/2);
      group.add(wBase);

      const wCrown = new this.THREE.Mesh(crownZGeoSolid, trimMat);
      wCrown.position.set(0, h - 0.06, -size/2);
      group.add(wCrown);

      this.addAABB(sData, cx, cz - size/2, size, wThick);
    }

    // --- 2. RENDER EAST WALL (Shared boundary at X = 10 with neighbor gx + 1) ---
    if (openE) {
      // Left Wall Partition (z = -5.75)
      const wLeft = new this.THREE.Mesh(splitWallGeoX, matWall);
      wLeft.position.set(size/2, h/2, -5.75);
      group.add(wLeft);

      const baseLeft = new this.THREE.Mesh(baseXGeoSplit, trimMat);
      baseLeft.position.set(size/2, 0.075, -5.75);
      group.add(baseLeft);

      const crownLeft = new this.THREE.Mesh(crownXGeoSplit, trimMat);
      crownLeft.position.set(size/2, h - 0.06, -5.75);
      group.add(crownLeft);

      this.addAABB(sData, cx + size/2, cz - 5.75, wThick, 8.5);

      // Right Wall Partition (z = 5.75)
      const wRight = new this.THREE.Mesh(splitWallGeoX, matWall);
      wRight.position.set(size/2, h/2, 5.75);
      group.add(wRight);

      const baseRight = new this.THREE.Mesh(baseXGeoSplit, trimMat);
      baseRight.position.set(size/2, 0.075, 5.75);
      group.add(baseRight);

      const crownRight = new this.THREE.Mesh(crownXGeoSplit, trimMat);
      crownRight.position.set(size/2, h - 0.06, 5.75);
      group.add(crownRight);

      this.addAABB(sData, cx + size/2, cz + 5.75, wThick, 8.5);

      // Header Wall Panel
      const wHeader = new this.THREE.Mesh(headerGeoX, matWall);
      wHeader.position.set(size/2, h - headerHeight/2, 0);
      group.add(wHeader);

      const crownHeader = new this.THREE.Mesh(new this.THREE.BoxGeometry(wThick + 0.04, 0.12, 3.0), trimMat);
      crownHeader.position.set(size/2, h - 0.06, 0);
      group.add(crownHeader);

      // Door Frame Casing (Solid Grey Metal marcos only, NO swung-open door panels!)
      const jambL = new this.THREE.Mesh(frameJambGeoX, frameCasingMat);
      jambL.position.set(size/2, 1.1, -1.5);
      group.add(jambL);

      const jambR = new this.THREE.Mesh(frameJambGeoX, frameCasingMat);
      jambR.position.set(size/2, 1.1, 1.5);
      group.add(jambR);

      const headerCasing = new this.THREE.Mesh(frameHeaderGeoX, frameCasingMat);
      headerCasing.position.set(size/2, 2.28, 0);
      group.add(headerCasing);

      // Mounting Glowing Sign Board
      const signBoard = new this.THREE.Mesh(signGeoX, signMat);
      signBoard.position.set(size/2 - 0.1, 2.45, 0);
      group.add(signBoard);
    } else {
      // Solid East Wall
      const w = new this.THREE.Mesh(solidWallGeoX, matWall);
      w.position.set(size/2, h/2, 0);
      group.add(w);

      const wBase = new this.THREE.Mesh(baseXGeoSolid, trimMat);
      wBase.position.set(size/2, 0.075, 0);
      group.add(wBase);

      const wCrown = new this.THREE.Mesh(crownXGeoSolid, trimMat);
      wCrown.position.set(size/2, h - 0.06, 0);
      group.add(wCrown);

      this.addAABB(sData, cx + size/2, cz, wThick, size);
    }

    // NOTE: South and West walls are NOT rendered by this sector!
    // They are rendered as the North and East walls of neighbor sectors (gx, gz + 1) and (gx - 1, gz) respectively.
    // This mathematically ensures every grid wall boundary is rendered EXACTLY ONCE, completely eliminating Z-fighting!
  }

  /**
   * Hotel impossible corridor layout: Creates narrow 3m halls.
   */
  buildCorridorWalls(group, cx, cz, h, matWall, matDoor, sData) {
    const size = this.sectorSize;
    const trimMat = this.textures.getMetalMaterial();
    
    // We construct walls defining a narrow central cross-shaped corridor
    // Center pathway is from x: -1.5 to 1.5, z: -1.5 to 1.5
    // Spawns 4 major wall partitions in corners
    const partitionGeo = new this.THREE.BoxGeometry(8.5, h, 8.5);
    const cornerBaseGeo = new this.THREE.BoxGeometry(8.54, 0.15, 8.54); // corridor corners baseboard
    const cornerCrownGeo = new this.THREE.BoxGeometry(8.54, 0.12, 8.54); // corridor corners crown molding

    const corners = [
      { x: -5.75, z: -5.75 }, { x: 5.75, z: -5.75 },
      { x: -5.75, z: 5.75 }, { x: 5.75, z: 5.75 }
    ];

    corners.forEach(c => {
      const part = new this.THREE.Mesh(partitionGeo, matWall);
      part.position.set(c.x, h/2, c.z);
      group.add(part);

      // Baseboard skirting
      const partBase = new this.THREE.Mesh(cornerBaseGeo, trimMat);
      partBase.position.set(c.x, 0.075, c.z);
      group.add(partBase);

      // Crown molding
      const partCrown = new this.THREE.Mesh(cornerCrownGeo, trimMat);
      partCrown.position.set(c.x, h - 0.06, c.z);
      group.add(partCrown);

      this.addAABB(sData, cx + c.x, cz + c.z, 8.5, 8.5);
    });

    // Add closed hotel bedroom doors along corridor sides
    const doorGeo = new this.THREE.BoxGeometry(0.1, 2.1, 0.9);
    
    const doors = [
      { x: -1.4, y: 1.05, z: -3, rot: 0 },
      { x: 1.4, y: 1.05, z: 3, rot: 0 },
      { x: -3, y: 1.05, z: -1.4, rot: Math.PI / 2 },
      { x: 3, y: 1.05, z: 1.4, rot: Math.PI / 2 }
    ];

    doors.forEach(d => {
      const dMesh = new this.THREE.Mesh(doorGeo, matDoor);
      dMesh.position.set(d.x, d.y, d.z);
      dMesh.rotation.y = d.rot;
      group.add(dMesh);

      // Gold brass room number
      const numGeo = new this.THREE.BoxGeometry(0.02, 0.05, 0.08);
      const numMat = new this.THREE.MeshStandardMaterial({ color: 0xe5c158, roughness: 0.1, metalness: 0.9 });
      const numMesh = new this.THREE.Mesh(numGeo, numMat);
      
      // Face outward depending on rot
      if (d.rot === 0) {
        numMesh.position.set(d.x + (d.x > 0 ? -0.06 : 0.06), 1.5, d.z);
      } else {
        numMesh.position.set(d.x, 1.5, d.z + (d.z > 0 ? -0.06 : 0.06));
        numMesh.rotation.y = Math.PI / 2;
      }
      group.add(numMesh);
    });
  }

  /**
   * Spawns an elevator shaft and sliding door assembly.
   */
  placeElevator(group, cx, cz, key, sData) {
    const h = this.ceilingHeight;
    const metalMat = this.textures.getMetalMaterial();
    const shaftWallMat = this.textures.getWallpaperMaterial('#3a3d40', '#2d3032'); // dark paneling

    // Build elevator recess on back wall (0, h/2, -9)
    const shaftGeo = new this.THREE.BoxGeometry(4.0, h, 2.0);
    const shaft = new this.THREE.Mesh(shaftGeo, shaftWallMat);
    shaft.position.set(0, h/2, -9);
    group.add(shaft);
    this.addAABB(sData, cx, cz - 9, 4.0, 2.0);

    // Elevator Doors (Pure solid grey steel panels, sliding)
    const doorPanelGeo = new this.THREE.BoxGeometry(0.95, 2.2, 0.05);
    const elevatorDoorMat = new this.THREE.MeshStandardMaterial({
      color: 0x7c7f85, // Clean retro solid grey
      roughness: 0.35,
      metalness: 0.8
    });
    
    const doorL = new this.THREE.Mesh(doorPanelGeo, elevatorDoorMat);
    doorL.position.set(-0.475, 1.1, -8.05);
    const doorR = new this.THREE.Mesh(doorPanelGeo, elevatorDoorMat);
    doorR.position.set(0.475, 1.1, -8.05);

    group.add(doorL);
    group.add(doorR);

    // Glass mirror backing inside elevator cabin
    const cabinMirrorGeo = new this.THREE.BoxGeometry(2.0, 2.2, 0.05);
    const cabinMirrorMat = new this.THREE.MeshStandardMaterial({
      color: 0xcccccc,
      roughness: 0.02,
      metalness: 1.0 // pure reflective
    });
    const cabinMirror = new this.THREE.Mesh(cabinMirrorGeo, cabinMirrorMat);
    cabinMirror.position.set(0, 1.1, -9.8);
    group.add(cabinMirror);

    // Call Signage panel above elevator
    const callSignGeo = new this.THREE.BoxGeometry(1.2, 0.4, 0.1);
    const callSignMat = this.textures.getSignageMaterial('elevator');
    const callSign = new this.THREE.Mesh(callSignGeo, callSignMat);
    callSign.position.set(0, 2.5, -7.95);
    group.add(callSign);

    // Track elevator state for animated opening
    this.elevators.push({
      sectorKey: key,
      cx: cx,
      cz: cz,
      doorL: doorL,
      doorR: doorR,
      state: 'closed', // 'closed', 'opening', 'open', 'closing'
      timer: 0,
      slideOffset: 0,
      soundDinged: false
    });
  }

  /**
   * Decorative potted plant.
   */
  /**
   * Decorative potted plant.
   */
  createPottedPlant() {
    const plantGroup = new this.THREE.Group();
    
    // Retro plant materials
    const dirtMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Dirt.png', 1, 1),
      roughness: 0.95
    });
    const potMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Rock.png', 1, 1),
      color: new this.THREE.Color('#dfdfdf'),
      roughness: 0.4
    });
    const leafMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Grass.png', 1, 1),
      color: new this.THREE.Color('#2e7d3b'), // beautiful dark green tint
      roughness: 0.8
    });

    // Pot
    const pot = new this.THREE.Mesh(
      new this.THREE.CylinderGeometry(0.4, 0.3, 0.7, 8),
      potMat
    );
    pot.position.y = 0.35;
    pot.castShadow = true;
    pot.receiveShadow = true;
    plantGroup.add(pot);

    // Soil layer inside pot
    const soil = new this.THREE.Mesh(
      new this.THREE.CylinderGeometry(0.38, 0.38, 0.05, 8),
      dirtMat
    );
    soil.position.y = 0.68;
    plantGroup.add(soil);

    // Leaves (procedural spheres/ellipsoids nested)
    for (let i = 0; i < 5; i++) {
      const leaf = new this.THREE.Mesh(
        new this.THREE.SphereGeometry(0.35, 6, 6),
        leafMat
      );
      leaf.scale.set(1, 0.4 + Math.random()*0.4, 1);
      leaf.position.set(
        (Math.random() - 0.5) * 0.3,
        0.7 + i * 0.15,
        (Math.random() - 0.5) * 0.3
      );
      leaf.rotation.x = Math.random() * 0.5;
      leaf.rotation.z = Math.random() * 0.5;
      plantGroup.add(leaf);
    }

    return plantGroup;
  }

  /**
   * Airport terminal passenger waiting chairs block.
   */
  createAirportBenches() {
    const bench = new this.THREE.Group();
    const metalMat = this.textures.getMetalMaterial();
    
    // Retro fabric terminal chairs
    const fabricMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Stone slabs.png', 1, 1),
      color: new this.THREE.Color('#3a485c'),
      roughness: 0.75
    });

    // 4 seats connected in a row
    const seatGeo = new this.THREE.BoxGeometry(0.7, 0.08, 0.6);
    const backGeo = new this.THREE.BoxGeometry(0.7, 0.6, 0.08);
    const beamGeo = new this.THREE.CylinderGeometry(0.05, 0.05, 3.2, 8);
    const legGeo = new this.THREE.CylinderGeometry(0.04, 0.04, 0.4, 6);

    // Support beams (rotated)
    const beam = new this.THREE.Mesh(beamGeo, metalMat);
    beam.rotation.z = Math.PI / 2;
    beam.position.set(0, 0.35, 0);
    bench.add(beam);

    // Legs
    const positionsX = [-1.4, 0, 1.4];
    positionsX.forEach(px => {
      const legF = new this.THREE.Mesh(legGeo, metalMat);
      legF.position.set(px, 0.2, 0.25);
      const legB = new this.THREE.Mesh(legGeo, metalMat);
      legB.position.set(px, 0.2, -0.25);
      bench.add(legF);
      bench.add(legB);
    });

    // 4 Seats
    for (let i = 0; i < 4; i++) {
      const sx = -1.2 + i * 0.8;
      
      const seat = new this.THREE.Mesh(seatGeo, fabricMat);
      seat.position.set(sx, 0.4, 0);
      
      const back = new this.THREE.Mesh(backGeo, fabricMat);
      back.position.set(sx, 0.7, -0.28);
      back.rotation.x = -Math.PI / 18; // slightly reclined

      bench.add(seat);
      bench.add(back);
    }

    return bench;
  }

  /**
   * Office cubicle block with panel walls, modular retro PC monitor, chair, keyboard.
   */
  createOfficeCubicle(matWall) {
    const cubeGroup = new this.THREE.Group();

    // Fabric partitions
    const fabricMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Sand.png', 1, 1),
      color: new this.THREE.Color('#5d6f7a'),
      roughness: 0.95
    });
    const panelGeo = new this.THREE.BoxGeometry(2.4, 1.5, 0.08);

    const backPanel = new this.THREE.Mesh(panelGeo, fabricMat);
    backPanel.position.set(0, 0.75, -1.2);
    
    const sidePanelL = new this.THREE.Mesh(panelGeo, fabricMat);
    sidePanelL.rotation.y = Math.PI / 2;
    sidePanelL.position.set(-1.2, 0.75, 0);

    const sidePanelR = new this.THREE.Mesh(panelGeo, fabricMat);
    sidePanelR.rotation.y = Math.PI / 2;
    sidePanelR.position.set(1.2, 0.75, 0);

    cubeGroup.add(backPanel);
    cubeGroup.add(sidePanelL);
    cubeGroup.add(sidePanelR);

    // Desk wooden top (mapped with N64 Wood planks)
    const deskMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Wood planks.png', 1, 1),
      color: new this.THREE.Color('#947758'), // Rich brown desk laminate
      roughness: 0.4
    });
    const deskTopGeo = new this.THREE.BoxGeometry(2.2, 0.05, 0.8);
    const deskTop = new this.THREE.Mesh(deskTopGeo, deskMat);
    deskTop.position.set(0, 0.75, -0.7);
    cubeGroup.add(deskTop);

    // Desk legs (low poly)
    const legGeo = new this.THREE.CylinderGeometry(0.04, 0.04, 0.75, 6);
    const legMat = this.textures.getMetalMaterial();
    const legs = [
      { x: -1.0, z: -0.4 }, { x: 1.0, z: -0.4 },
      { x: -1.0, z: -1.0 }, { x: 1.0, z: -1.0 }
    ];
    legs.forEach(l => {
      const leg = new this.THREE.Mesh(legGeo, legMat);
      leg.position.set(l.x, 0.375, l.z);
      cubeGroup.add(leg);
    });

    // Retro PC Monitor utilizing our beautiful modular createRetroPC()
    const pcSetup = this.createRetroPC();
    pcSetup.position.set(-0.3, 0.77, -0.7);
    pcSetup.rotation.y = Math.PI / 10; // angled screen
    cubeGroup.add(pcSetup);

    return cubeGroup;
  }

  /**
   * Helper to create a single modular retro 90s CRT PC setup (monitor, CPU tower, keyboard, mouse).
   */
  createRetroPC() {
    const pcGroup = new this.THREE.Group();
    const bodyMat = new this.THREE.MeshStandardMaterial({
      color: 0xdfded9, // classic retro beige-grey
      roughness: 0.6,
      metalness: 0.1
    });
    const screenMat = new this.THREE.MeshStandardMaterial({
      color: 0x0c0d10, // dark reflective glass
      roughness: 0.1,
      metalness: 0.8
    });
    const plasticDark = new this.THREE.MeshStandardMaterial({
      color: 0x333333,
      roughness: 0.7
    });

    // 1. CRT Monitor
    const monitor = new this.THREE.Group();
    
    // Curved back box housing
    const housing = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.4, 0.3, 0.28), bodyMat);
    housing.position.y = 0.25;
    monitor.add(housing);

    // Dark screen overlay
    const screen = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.36, 0.26, 0.01), screenMat);
    screen.position.set(0, 0.25, 0.142);
    monitor.add(screen);

    // Stand base
    const stand = new this.THREE.Mesh(new this.THREE.CylinderGeometry(0.04, 0.06, 0.1, 8), bodyMat);
    stand.position.set(0, 0.05, 0);
    monitor.add(stand);

    monitor.position.set(0, 0, 0);
    pcGroup.add(monitor);

    // 2. CPU Desktop Tower (lying horizontally next to monitor)
    const tower = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.18, 0.36, 0.36), bodyMat);
    tower.position.set(0.36, 0.18, -0.05);
    pcGroup.add(tower);

    // Tiny details on tower front face
    const driveGeo = new this.THREE.BoxGeometry(0.12, 0.04, 0.01);
    const floppyDrive = new this.THREE.Mesh(driveGeo, plasticDark);
    floppyDrive.position.set(0.36, 0.28, 0.132);
    pcGroup.add(floppyDrive);

    const buttonGeo = new this.THREE.BoxGeometry(0.02, 0.02, 0.01);
    const powerBtn = new this.THREE.Mesh(buttonGeo, new this.THREE.MeshBasicMaterial({ color: 0x39ff14 })); // green led
    powerBtn.position.set(0.36, 0.08, 0.132);
    pcGroup.add(powerBtn);

    const cdTray = new this.THREE.Mesh(driveGeo, plasticDark);
    cdTray.position.set(0.36, 0.22, 0.132);
    pcGroup.add(cdTray);

    // 3. Keyboard
    const keyboard = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.38, 0.02, 0.14), bodyMat);
    keyboard.position.set(-0.05, 0.01, 0.26);
    // Dark keys grid simulation
    const keys = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.34, 0.005, 0.1), plasticDark);
    keys.position.set(-0.05, 0.021, 0.26);
    pcGroup.add(keyboard);
    pcGroup.add(keys);

    // 4. Mouse
    const mouse = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.04, 0.018, 0.07), bodyMat);
    mouse.position.set(0.22, 0.01, 0.24);
    pcGroup.add(mouse);

    // Enable shadows on all child meshes
    pcGroup.traverse(node => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    return pcGroup;
  }

  /**
   * Generates a cluttered stack/pile of old, obsolete CRT monitors, CPUs, keyboards, and tipped desks.
   */
  createCRTPile() {
    const pile = new this.THREE.Group();
    const randomAngle = () => (Math.random() - 0.5) * 0.4;

    // 1. Slanted base computer desk tipped over on its side
    const deskMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Wood planks.png', 1, 1),
      color: new this.THREE.Color('#5a4230'),
      roughness: 0.6
    });
    const trimMat = this.textures.getMetalMaterial();

    const tippedDesk = new this.THREE.Group();
    const deskTop = new this.THREE.Mesh(new this.THREE.BoxGeometry(2.2, 0.05, 0.8), deskMat);
    deskTop.rotation.z = Math.PI / 4; // Tilted 45 degrees
    deskTop.position.set(-0.4, 0.7, 0);
    tippedDesk.add(deskTop);

    // Desk legs pointing in weird angles
    const legGeo = new this.THREE.CylinderGeometry(0.04, 0.04, 0.75, 6);
    const leg1 = new this.THREE.Mesh(legGeo, trimMat);
    leg1.position.set(-1.0, 0.4, -0.3);
    leg1.rotation.z = Math.PI / 3;
    tippedDesk.add(leg1);

    const leg2 = new this.THREE.Mesh(legGeo, trimMat);
    leg2.position.set(0.2, 0.9, 0.3);
    leg2.rotation.z = -Math.PI / 6;
    tippedDesk.add(leg2);

    pile.add(tippedDesk);

    // 2. Pile of stacked CRT Computers and CPU Towers under/around the desk
    // Stack 1: Bottom CPU, Middle CRT, Top Tipped CRT
    const pc1 = this.createRetroPC();
    pc1.position.set(-0.2, 0.0, -0.4);
    pc1.rotation.y = randomAngle() + Math.PI / 6;
    pile.add(pc1);

    // Stack 2: A CPU tower standing vertically on the floor, supporting a tipped monitor
    const pc2 = this.createRetroPC();
    pc2.position.set(0.6, 0.0, 0.2);
    pc2.rotation.y = randomAngle() - Math.PI / 3;
    pc2.rotation.z = Math.PI / 2; // tipped on its side!
    pc2.position.y = 0.1;
    pile.add(pc2);

    // Stack 3: Just a loose CRT Monitor lying face-down
    const loosePC = this.createRetroPC();
    loosePC.position.set(-0.8, 0.0, 0.3);
    loosePC.rotation.x = Math.PI / 2.2; // face down!
    loosePC.rotation.y = Math.PI / 4;
    pile.add(loosePC);

    // Stack 4: Obsolete monitors stacked high in a column
    const highPC1 = this.createRetroPC();
    highPC1.position.set(-0.1, 0.0, 0.4);
    highPC1.rotation.y = Math.PI / 12;
    pile.add(highPC1);

    const highPC2 = this.createRetroPC();
    highPC2.position.set(-0.25, 0.45, -0.35); // stacked directly on pc1 monitor!
    highPC2.rotation.y = -Math.PI / 5;
    highPC2.rotation.x = 0.2; // slightly slanted forward
    pile.add(highPC2);

    // Stack 5: A CPU tower leaning against the tipped desk leg
    const loneTower = new this.THREE.Mesh(
      new this.THREE.BoxGeometry(0.18, 0.36, 0.36),
      new this.THREE.MeshStandardMaterial({ color: 0xdfded9, roughness: 0.6 })
    );
    loneTower.position.set(-0.7, 0.18, -0.2);
    loneTower.rotation.y = -Math.PI / 4;
    loneTower.rotation.x = 0.3; // leaning!
    pile.add(loneTower);

    return pile;
  }

  /**
   * Spawns realistic 3D fluorescent tube light fixtures on ceiling in a deterministic grid layout.
   * Shifted inward based on zoneType to prevent overlaps with columns/pillars and rotated parallel to paths.
   */
  addGridCeilingLights(group, cx, cz, height, gx, gz) {
    const trimMat = this.textures.getMetalMaterial();
    const baseLightMat = this.textures.getCeilingLightMaterial();

    const lightHash = Math.abs(this.hash2D(gx, gz));
    const isFlickering = (lightHash % 12 === 0);
    const zoneType = lightHash % 10;

    // Dynamic cloning to isolate flickering lighting emission without wasting GPU memory!
    const lightMat = isFlickering ? baseLightMat.clone() : baseLightMat;

    // 3D Fluorescent Tube Light Fixture Model
    const fixtureGeo = new this.THREE.BoxGeometry(0.4, 0.08, 2.4);
    const tubeGeo = new this.THREE.CylinderGeometry(0.024, 0.024, 2.2, 8);
    const capGeo = new this.THREE.CylinderGeometry(0.028, 0.028, 0.08, 8);

    // Coordinate calculation: dynamically avoid column overlaps
    let lightCoords = [];
    if (zoneType === 0 || zoneType === 4 || zoneType === 5 || zoneType === 6 || zoneType === 9) {
      // Lobby, Office, Library, Data Center, Gallery: shift inward to avoid columns and props
      lightCoords = [
        { x: -3.2, z: -3.2 }, { x: 3.2, z: -3.2 },
        { x: -3.2, z: 3.2 }, { x: 3.2, z: 3.2 }
      ];
    } else if (zoneType === 2) {
      // Hotel: Align lights exactly down the center corridors
      lightCoords = [
        { x: 0, z: -4.5, isVert: true },
        { x: 0, z: 4.5, isVert: true },
        { x: -4.5, z: 0, isVert: false },
        { x: 4.5, z: 0, isVert: false }
      ];
    } else if (zoneType === 7) {
      // Poolrooms: Align over the tiled walk deck corners (avoiding central pool area)
      lightCoords = [
        { x: -6.0, z: -6.0 }, { x: 6.0, z: -6.0 },
        { x: -6.0, z: 6.0 }, { x: 6.0, z: 6.0 }
      ];
    } else {
      // Mall, Airport, Greenhouse: align lights beautifully in intermediate positions
      lightCoords = [
        { x: -4.5, z: -4.5 }, { x: 4.5, z: -4.5 },
        { x: -4.5, z: 4.5 }, { x: 4.5, z: 4.5 }
      ];
    }

    let representativeMesh = null;

    lightCoords.forEach((lc) => {
      const fixtureGroup = new this.THREE.Group();
      fixtureGroup.position.set(lc.x, height, lc.z);

      // Rotate light parallel to the horizontal corridor if needed
      if (lc.isVert === false) {
        fixtureGroup.rotation.y = Math.PI / 2;
      }

      // 1. Fixture Base (Brushed Metal)
      const base = new this.THREE.Mesh(fixtureGeo, trimMat);
      base.position.y = -0.04;
      fixtureGroup.add(base);

      // 2. Dual Glowing Tubes (Emissive glass)
      const tubeL = new this.THREE.Mesh(tubeGeo, lightMat);
      tubeL.rotation.x = Math.PI / 2;
      tubeL.position.set(-0.08, -0.09, 0);
      fixtureGroup.add(tubeL);

      const tubeR = new this.THREE.Mesh(tubeGeo, lightMat);
      tubeR.rotation.x = Math.PI / 2;
      tubeR.position.set(0.08, -0.09, 0);
      fixtureGroup.add(tubeR);

      // Keep reference to one of the tube meshes to modulate flickering
      representativeMesh = tubeL;

      // 3. Metal End Caps
      const endCaps = [
        { z: -1.1 }, { z: 1.1 }
      ];
      endCaps.forEach(cap => {
        const cap1 = new this.THREE.Mesh(capGeo, trimMat);
        cap1.rotation.x = Math.PI / 2;
        cap1.position.set(-0.08, -0.09, cap.z);
        
        const cap2 = new this.THREE.Mesh(capGeo, trimMat);
        cap2.rotation.x = Math.PI / 2;
        cap2.position.set(0.08, -0.09, cap.z);

        fixtureGroup.add(cap1);
        fixtureGroup.add(cap2);
      });

      group.add(fixtureGroup);
    });

    // Spawn exactly 1 central PointLight for the whole sector (Dramatically optimizes GPU rendering!)
    const pointLight = new this.THREE.PointLight(0xfff5e0, 2.2, 25);
    pointLight.position.set(0, height - 0.5, 0);
    pointLight.castShadow = false;
    group.add(pointLight);

    if (isFlickering && representativeMesh) {
      this.flickeringLights.push({
        sectorKey: `${gx},${gz}`,
        pointLight: pointLight,
        mesh: representativeMesh,
        baseIntensity: 2.2,
        baseEmissive: 1.8,
        timer: 0
      });
    }
  }

  /* --- DATA AND UTILITY HELPERS --- */

  /**
   * Adds an axis-aligned bounding box to the collision checklist.
   */
  addAABB(sector, x, z, width, depth) {
    sector.collisionBoxes.push({
      minX: x - width / 2,
      maxX: x + width / 2,
      minZ: z - depth / 2,
      maxZ: z + depth / 2
    });
  }

  /**
   * Generates a beautifully detailed 90s console retro airport plane ticket shop counter,
   * containing desk counters, translucent safety glass, support columns, dual CRT PC stations,
   * and a vibrant cyan glowing backing sign "TICKETS & BOARDING".
   */
  createTicketShop() {
    const shopGroup = new this.THREE.Group();

    // 1. Desk Counter
    const deskMat = new this.THREE.MeshStandardMaterial({
      color: 0x1f2937, // dark slate blue/grey
      roughness: 0.2,
      metalness: 0.8
    });
    const counterDesk = new this.THREE.Mesh(new this.THREE.BoxGeometry(4.5, 1.1, 1.2), deskMat);
    counterDesk.position.set(0, 0.55, 0);
    shopGroup.add(counterDesk);

    // 2. Glass Shields (Security partitions standard in old ticket counters)
    const glassMat = new this.THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.25,
      roughness: 0.05,
      metalness: 0.1,
      transmission: 0.95,
      ior: 1.5,
      thickness: 0.05
    });
    const metalMat = this.textures.getMetalMaterial();

    const glassGeo = new this.THREE.BoxGeometry(4.2, 0.9, 0.08);
    const glass = new this.THREE.Mesh(glassGeo, glassMat);
    glass.position.set(0, 1.1 + 0.45, 0); // rest on counter, centered
    shopGroup.add(glass);

    // Glass metal framing supports (left, center, right vertical bars)
    const barGeo = new this.THREE.CylinderGeometry(0.02, 0.02, 0.9, 8);
    const supportL = new this.THREE.Mesh(barGeo, metalMat);
    supportL.position.set(-2.1, 1.1 + 0.45, 0);
    const supportC = new this.THREE.Mesh(barGeo, metalMat);
    supportC.position.set(0, 1.1 + 0.45, 0.05); // slightly in front to support glass seam
    const supportR = new this.THREE.Mesh(barGeo, metalMat);
    supportR.position.set(2.1, 1.1 + 0.45, 0);

    shopGroup.add(supportL);
    shopGroup.add(supportC);
    shopGroup.add(supportR);

    // 3. Glowing Sign Board: "TICKETS & BOARDING" (cyan glow)
    const signMat = this.textures.getDoorwayLabelMaterial("TICKETS & BOARDING", "#00bcd4");
    const signBoard = new this.THREE.Mesh(new this.THREE.BoxGeometry(3.2, 0.6, 0.15), signMat);
    signBoard.position.set(0, 2.7, 0);
    shopGroup.add(signBoard);

    // Sign support columns
    const signColGeo = new this.THREE.CylinderGeometry(0.04, 0.04, 2.0, 8);
    const colL = new this.THREE.Mesh(signColGeo, metalMat);
    colL.position.set(-1.2, 1.1 + 1.0, -0.2);
    const colR = new this.THREE.Mesh(signColGeo, metalMat);
    colR.position.set(1.2, 1.1 + 1.0, -0.2);

    shopGroup.add(colL);
    shopGroup.add(colR);

    // 4. Dual retro operator workstations facing inward
    const pc1 = this.createRetroPC();
    pc1.position.set(-1.0, 1.1, -0.2);
    pc1.rotation.y = Math.PI; // Face operator behind counter
    shopGroup.add(pc1);

    const pc2 = this.createRetroPC();
    pc2.position.set(1.0, 1.1, -0.2);
    pc2.rotation.y = Math.PI; // Face operator behind counter
    shopGroup.add(pc2);

    // Apply shadows to all meshes inside group
    shopGroup.traverse(node => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    return shopGroup;
  }

  /**
   * ZONE 5: Biblioteca Interminable (Dark wood floor, warm bookshelves, study tables, green banking lamps)
   */
  buildLibrarySector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    const matFloor = this.textures.getWoodPlanksMaterial('#3d2414'); // dark mahogany planks
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#5c4230', '#423022'); // warm brown vintage wallpaper

    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 0); // fallback to zone 0 border layout

    // Bookshelves columns layout
    const shelfPositions = [
      { x: -4.5, z: -4.5, rot: 0 },
      { x: 4.5, z: -4.5, rot: 0 },
      { x: -4.5, z: 4.5, rot: 0 },
      { x: 4.5, z: 4.5, rot: 0 }
    ];

    shelfPositions.forEach(pos => {
      const shelf = this.createLibraryBookshelf();
      shelf.position.set(pos.x, 0, pos.z);
      shelf.rotation.y = pos.rot;
      group.add(shelf);

      // Bookshelf AABB: width is 2.2 along X, depth is 0.8 along Z
      this.addAABB(sData, cx + pos.x, cz + pos.z, 2.2, 0.8);
    });

    // Central study table (Only on non-starting sectors to avoid spawn blockage)
    const isStart = (sData.gx === this.startGx && sData.gz === this.startGz);
    if (!isStart) {
      const tableGroup = new this.THREE.Group();
      tableGroup.position.set(0, 0, 0);

      // Tabletop
      const tableMat = this.textures.getWoodPlanksMaterial('#5a3d28');
      const tabletop = new this.THREE.Mesh(new this.THREE.BoxGeometry(2.8, 0.06, 1.2), tableMat);
      tabletop.position.y = 0.75;
      tableGroup.add(tabletop);

      // Legs
      const legGeo = new this.THREE.CylinderGeometry(0.04, 0.04, 0.75, 6);
      const legMat = this.textures.getMetalMaterial();
      const legs = [
        { x: -1.2, z: -0.45 }, { x: 1.2, z: -0.45 },
        { x: -1.2, z: 0.45 }, { x: 1.2, z: 0.45 }
      ];
      legs.forEach(l => {
        const leg = new this.THREE.Mesh(legGeo, legMat);
        leg.position.set(l.x, 0.375, l.z);
        tableGroup.add(leg);
      });

      // Emissive green banking lamp
      const lampPost = new this.THREE.Mesh(new this.THREE.CylinderGeometry(0.015, 0.015, 0.3, 6), legMat);
      lampPost.position.set(0, 0.9, 0);
      tableGroup.add(lampPost);

      const lampHoodMat = new this.THREE.MeshStandardMaterial({
        color: 0x2e7d3b,
        emissive: 0x39ff14,
        emissiveIntensity: 1.8,
        roughness: 0.2
      });
      const lampHood = new this.THREE.Mesh(new this.THREE.CylinderGeometry(0.12, 0.18, 0.08, 8), lampHoodMat);
      lampHood.position.set(0, 1.05, 0);
      tableGroup.add(lampHood);

      // Soft lamp light pointing down
      const lampLight = new this.THREE.PointLight(0x7cff7c, 1.2, 5);
      lampLight.position.set(0, 0.95, 0);
      tableGroup.add(lampLight);

      group.add(tableGroup);
      this.addAABB(sData, cx, cz, 2.8, 1.2);
    }

    this.buildChamberWalls(group, cx, cz, h, matWall, !wallN, !wallS, !wallE, !wallW, sData, 5);
    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /**
   * ZONE 6: Centro de Datos Silencioso (Grey metal grids floor, dark panel walls, flashing server racks)
   */
  buildDataSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    const matFloor = this.textures.getMetalMaterial(); // industrial steel panels
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#222428', '#141518'); // dark carbon steel panels

    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 4); // fallback to corporate layout

    // Rows of server racks
    const serverPositions = [
      { x: -4.5, z: -3.5 }, { x: -4.5, z: 3.5 },
      { x: 4.5, z: -3.5 }, { x: 4.5, z: 3.5 }
    ];

    serverPositions.forEach(pos => {
      const rack = this.createServerRack();
      rack.position.set(pos.x, 0, pos.z);
      group.add(rack);

      this.addAABB(sData, cx + pos.x, cz + pos.z, 1.0, 1.0);
    });

    this.buildChamberWalls(group, cx, cz, h, matWall, !wallN, !wallS, !wallE, !wallW, sData, 6);
    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /**
   * ZONE 7: Piscina Subterránea / Poolrooms (Turquoise tiles, pristine pillars, recessed water pool)
   */
  buildPoolSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const tallH = 7.5; // High pool ceiling

    // Textures
    const matTileWhite = this.textures.getTerrazzoMaterial('#f2fbfb'); // clean white ceramic tiles
    const matTileTurquoise = this.textures.getTerrazzoMaterial('#b2ebf2'); // soft aqua/cyan tiles
    const matCeiling = this.textures.getCeilingMaterial();

    // 1. Raised Walk Decks surrounding central pool
    const deckH = 0.2;
    const deckGeoN = new this.THREE.BoxGeometry(size, deckH, 7.0);
    const deckGeoS = new this.THREE.BoxGeometry(size, deckH, 7.0);
    const deckGeoE = new this.THREE.BoxGeometry(7.0, deckH, 6.0);
    const deckGeoW = new this.THREE.BoxGeometry(7.0, deckH, 6.0);

    const deckN = new this.THREE.Mesh(deckGeoN, matTileWhite);
    deckN.position.set(0, deckH/2, -6.5);
    deckN.receiveShadow = true;
    group.add(deckN);

    const deckS = new this.THREE.Mesh(deckGeoS, matTileWhite);
    deckS.position.set(0, deckH/2, 6.5);
    deckS.receiveShadow = true;
    group.add(deckS);

    const deckE = new this.THREE.Mesh(deckGeoE, matTileWhite);
    deckE.position.set(6.5, deckH/2, 0);
    deckE.receiveShadow = true;
    group.add(deckE);

    const deckW = new this.THREE.Mesh(deckGeoW, matTileWhite);
    deckW.position.set(-6.5, deckH/2, 0);
    deckW.receiveShadow = true;
    group.add(deckW);

    // Ceiling plane
    const ceilingGeo = new this.THREE.PlaneGeometry(size, size);
    const ceiling = new this.THREE.Mesh(ceilingGeo, matCeiling);
    ceiling.rotation.x = Math.PI / 2;
    ceiling.position.y = tallH;
    group.add(ceiling);

    // 2. Central Recessed Water Pool
    const poolBottom = new this.THREE.Mesh(new this.THREE.PlaneGeometry(6.0, 6.0), matTileTurquoise);
    poolBottom.rotation.x = -Math.PI / 2;
    poolBottom.position.set(0, -1.2, 0);
    poolBottom.receiveShadow = true;
    group.add(poolBottom);

    // Pool vertical walls
    const poolWallMat = matTileTurquoise;
    const pWallGeoH = new this.THREE.BoxGeometry(6.0, 1.4, 0.1);
    const pWallGeoV = new this.THREE.BoxGeometry(0.1, 1.4, 6.0);

    const pWallN = new this.THREE.Mesh(pWallGeoH, poolWallMat);
    pWallN.position.set(0, -0.6, -3.0);
    group.add(pWallN);

    const pWallS = new this.THREE.Mesh(pWallGeoH, poolWallMat);
    pWallS.position.set(0, -0.6, 3.0);
    group.add(pWallS);

    const pWallE = new this.THREE.Mesh(pWallGeoV, poolWallMat);
    pWallE.position.set(3.0, -0.6, 0);
    group.add(pWallE);

    const pWallW = new this.THREE.Mesh(pWallGeoV, poolWallMat);
    pWallW.position.set(-3.0, -0.6, 0);
    group.add(pWallW);

    // 3. Physical Transparent Water Plane
    const waterMat = new this.THREE.MeshPhysicalMaterial({
      color: 0x00f5ff,
      transparent: true,
      opacity: 0.55,
      roughness: 0.08,
      metalness: 0.1,
      transmission: 0.88,
      ior: 1.333,
      thickness: 0.2
    });
    const water = new this.THREE.Mesh(new this.THREE.PlaneGeometry(6.0, 6.0), waterMat);
    water.rotation.x = -Math.PI / 2;
    water.position.set(0, -0.1, 0);
    group.add(water);

    // 4. White Tile Pillars supporting the high roof
    const pillarGeo = new this.THREE.BoxGeometry(0.8, tallH, 0.8);
    const pillars = [
      { x: -3.2, z: -3.2 }, { x: 3.2, z: -3.2 },
      { x: -3.2, z: 3.2 }, { x: 3.2, z: 3.2 }
    ];

    pillars.forEach(p => {
      const pil = new this.THREE.Mesh(pillarGeo, matTileWhite);
      pil.position.set(p.x, tallH/2, p.z);
      pil.castShadow = true;
      pil.receiveShadow = true;
      group.add(pil);

      this.addAABB(sData, cx + p.x, cz + p.z, 0.8, 0.8);
    });

    // Secure boundary: block the central pool entirely to prevent player from falling out of bound/floating!
    this.addAABB(sData, cx, cz, 6.0, 6.0);

    this.buildChamberWalls(group, cx, cz, tallH, matTileWhite, !wallN, !wallS, !wallE, !wallW, sData, 7);
    this.addGridCeilingLights(group, cx, cz, tallH, sData.gx, sData.gz);
  }

  /**
   * ZONE 8: Invernadero Vacío (Terracotta floor, steel-frame glass walls, planters with ferns)
   */
  buildGreenhouseSector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const tallH = 7.5; // High conservatory ceiling

    const matFloor = this.textures.getCarpetMaterial('#9c4a2a'); // pixel terracotta tiles
    const matCeiling = this.textures.getCeilingMaterial();

    this.addFloorAndCeiling(group, size, tallH, matFloor, matCeiling, sData.gx, sData.gz, 3); // airport border fallback

    // Translucent Physical Glass Wall panels inside grey steel frame
    const matGlassWall = new this.THREE.MeshPhysicalMaterial({
      color: 0xd5e8e6,
      transparent: true,
      opacity: 0.22,
      roughness: 0.08,
      metalness: 0.8,
      transmission: 0.82,
      ior: 1.52,
      thickness: 0.15
    });

    // Props: overgrown metal planters
    const planterPositions = [
      { x: -4.5, z: 0 },
      { x: 4.5, z: 0 }
    ];

    planterPositions.forEach(pos => {
      const planter = this.createPlanterBox();
      planter.position.set(pos.x, 0, pos.z);
      group.add(planter);

      this.addAABB(sData, cx + pos.x, cz + pos.z, 3.2, 1.0);
    });

    // Render conservatory glass perimeter walls
    this.buildChamberWalls(group, cx, cz, tallH, matGlassWall, !wallN, !wallS, !wallE, !wallW, sData, 8);
    this.addGridCeilingLights(group, cx, cz, tallH, sData.gx, sData.gz);
  }

  /**
   * ZONE 9: Galería de Arte Infinita (Glossy white marble, white drywalls, hanging paintings)
   */
  buildGallerySector(group, cx, cz, wallN, wallS, wallE, wallW, sData) {
    const size = this.sectorSize;
    const h = this.ceilingHeight;

    const matFloor = this.textures.getTerrazzoMaterial('#ffffff'); // high-gloss white marble
    const matCeiling = this.textures.getCeilingMaterial();
    const matWall = this.textures.getWallpaperMaterial('#fdfdfd', '#f3f4f6'); // white gallery gypsum drywall

    this.addFloorAndCeiling(group, size, h, matFloor, matCeiling, sData.gx, sData.gz, 1); // Mall terrazzo border layout fallback

    // Partition walls with abstract paintings
    const partitions = [
      { x: -4.5, z: -2.0, rot: 0 },
      { x: 4.5, z: 2.0, rot: Math.PI / 2 }
    ];

    partitions.forEach(pos => {
      const p = this.createPainting();
      p.position.set(pos.x, 0, pos.z);
      p.rotation.y = pos.rot;
      group.add(p);

      // Partition width is 4.5, depth is 0.4.
      if (pos.rot === 0) {
        this.addAABB(sData, cx + pos.x, cz + pos.z, 4.5, 0.4);
      } else {
        this.addAABB(sData, cx + pos.x, cz + pos.z, 0.4, 4.5);
      }
    });

    this.buildChamberWalls(group, cx, cz, h, matWall, !wallN, !wallS, !wallE, !wallW, sData, 9);
    this.addGridCeilingLights(group, cx, cz, h, sData.gx, sData.gz);
  }

  /**
   * Generates a double-sided wooden library bookshelf packed with colorful N64 book box blocks.
   */
  createLibraryBookshelf() {
    const bookshelf = new this.THREE.Group();

    // High wooden cabinet body
    const cabinetMat = this.textures.getWoodPlanksMaterial('#3d2414');
    const casing = new this.THREE.Mesh(new this.THREE.BoxGeometry(2.2, 2.8, 0.8), cabinetMat);
    casing.position.y = 1.4;
    bookshelf.add(casing);

    // Book meshes stacked in shelves (4 shelves, front and back sides)
    const colors = ['#a32a2a', '#2e5e8a', '#2e7d3b', '#dfc07d', '#5a4230', '#7a2267'];
    
    for (let shelf = 0; shelf < 4; shelf++) {
      const sy = 0.35 + shelf * 0.65;
      
      // Place books along width
      for (let b = 0; b < 7; b++) {
        const bx = -0.85 + b * 0.28 + (Math.random() - 0.5) * 0.05;
        const bw = 0.12 + Math.random() * 0.1;
        const bh = 0.38 + Math.random() * 0.18;
        const bd = 0.26;
        
        const bookMat = new this.THREE.MeshStandardMaterial({
          color: new this.THREE.Color(colors[Math.floor(Math.random() * colors.length)]),
          roughness: 0.82
        });

        // Front book
        const bookF = new this.THREE.Mesh(new this.THREE.BoxGeometry(bw, bh, bd), bookMat);
        bookF.position.set(bx, sy, 0.28);
        bookshelf.add(bookF);

        // Back book
        const bookB = new this.THREE.Mesh(new this.THREE.BoxGeometry(bw, bh, bd), bookMat);
        bookB.position.set(bx, sy, -0.28);
        bookshelf.add(bookB);
      }
    }

    bookshelf.traverse(node => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    return bookshelf;
  }

  /**
   * Generates a premium dark steel computer server cabinet rack, loaded with blue/green neon glowing LED rails.
   */
  createServerRack() {
    const rackGroup = new this.THREE.Group();
    const metalMat = this.textures.getMetalMaterial();

    const cabinetMat = new this.THREE.MeshStandardMaterial({
      color: 0x111215,
      roughness: 0.18,
      metalness: 0.88
    });

    // Cabinet body
    const cabinet = new this.THREE.Mesh(new this.THREE.BoxGeometry(1.0, 3.2, 1.0), cabinetMat);
    cabinet.position.y = 1.6;
    rackGroup.add(cabinet);

    // Frame metal contours
    const frameGeoH = new this.THREE.BoxGeometry(1.04, 0.06, 1.04);
    const topFrame = new this.THREE.Mesh(frameGeoH, metalMat);
    topFrame.position.y = 3.17;
    rackGroup.add(topFrame);

    const baseFrame = new this.THREE.Mesh(frameGeoH, metalMat);
    baseFrame.position.y = 0.03;
    rackGroup.add(baseFrame);

    // Tiny flashing led components on front grid
    const ledBlue = new this.THREE.MeshBasicMaterial({ color: 0x007aff });
    const ledGreen = new this.THREE.MeshBasicMaterial({ color: 0x39ff14 });
    const ledCyan = new this.THREE.MeshBasicMaterial({ color: 0x00ffff });

    for (let row = 0; row < 8; row++) {
      const ly = 0.4 + row * 0.35;
      
      const lMatL = Math.random() > 0.4 ? ledBlue : ledGreen;
      const lMatR = Math.random() > 0.5 ? ledCyan : ledGreen;

      const ledL = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.05, 0.03, 0.02), lMatL);
      ledL.position.set(-0.25, ly, 0.505);
      
      const ledR = new this.THREE.Mesh(new this.THREE.BoxGeometry(0.05, 0.03, 0.02), lMatR);
      ledR.position.set(0.25, ly, 0.505);

      rackGroup.add(ledL);
      rackGroup.add(ledR);
    }

    rackGroup.traverse(node => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    return rackGroup;
  }

  /**
   * Generates a modern conservatory metal planter container filled with procedural low-poly plant meshes.
   */
  createPlanterBox() {
    const planter = new this.THREE.Group();
    const metalMat = this.textures.getMetalMaterial();

    // Planter steel trough base
    const trough = new this.THREE.Mesh(new this.THREE.BoxGeometry(3.2, 0.6, 1.0), metalMat);
    trough.position.y = 0.3;
    planter.add(trough);

    // Soil
    const soilMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Dirt.png', 1, 1),
      roughness: 0.95
    });
    const soil = new this.THREE.Mesh(new this.THREE.BoxGeometry(3.1, 0.05, 0.9), soilMat);
    soil.position.y = 0.58;
    planter.add(soil);

    // Grow 4 plants along planter length
    const leafMat = new this.THREE.MeshStandardMaterial({
      map: this.textures.loadRetroTexture('Grass.png', 1, 1),
      color: new this.THREE.Color('#2c7a3c'), // nice deep fern green
      roughness: 0.8
    });

    for (let plant = 0; plant < 4; plant++) {
      const px = -1.2 + plant * 0.8 + (Math.random() - 0.5) * 0.12;
      
      // clustered leaf layers
      for (let i = 0; i < 4; i++) {
        const leaf = new this.THREE.Mesh(new this.THREE.SphereGeometry(0.28, 6, 6), leafMat);
        leaf.scale.set(1.1, 0.4 + Math.random() * 0.4, 1.1);
        leaf.position.set(
          px + (Math.random() - 0.5) * 0.15,
          0.6 + i * 0.14,
          (Math.random() - 0.5) * 0.15
        );
        leaf.rotation.x = Math.random() * 0.4;
        leaf.rotation.z = Math.random() * 0.4;
        planter.add(leaf);
      }
    }

    planter.traverse(node => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    return planter;
  }

  /**
   * Generates a freestanding art gallery partition wall holding beautifully framed retro N64 paintings.
   */
  createPainting() {
    const partition = new this.THREE.Group();
    const metalMat = this.textures.getMetalMaterial();

    // Freestanding white partition wall
    const wallMat = this.textures.getWallpaperMaterial('#fdfdfd', '#fafbfc');
    const board = new this.THREE.Mesh(new this.THREE.BoxGeometry(4.5, 2.8, 0.4), wallMat);
    board.position.y = 1.4;
    partition.add(board);

    // Steel base skirting
    const skirting = new this.THREE.Mesh(new this.THREE.BoxGeometry(4.54, 0.15, 0.44), metalMat);
    skirting.position.y = 0.075;
    partition.add(skirting);

    // Frame (North face, z = 0.21)
    const frameMat = this.textures.getWoodPlanksMaterial('#1f140e'); // dark mahogany
    const frameF = new this.THREE.Mesh(new this.THREE.BoxGeometry(1.6, 1.2, 0.06), frameMat);
    frameF.position.set(0, 1.5, 0.22);
    partition.add(frameF);

    // Canvas (North)
    const artTexF = this.textures.loadRetroTexture('Crystal 3.png', 1, 1);
    const canvasMatF = new this.THREE.MeshStandardMaterial({
      map: artTexF,
      color: new this.THREE.Color('#d500f9'), // rich pink/violet console vector
      emissive: new this.THREE.Color('#4a0072'),
      emissiveIntensity: 0.55,
      roughness: 0.25
    });
    const canvasF = new this.THREE.Mesh(new this.THREE.BoxGeometry(1.4, 1.0, 0.02), canvasMatF);
    canvasF.position.set(0, 1.5, 0.25);
    partition.add(canvasF);

    // Frame (South face, z = -0.21)
    const frameB = new this.THREE.Mesh(new this.THREE.BoxGeometry(1.6, 1.2, 0.06), frameMat);
    frameB.position.set(0, 1.5, -0.22);
    partition.add(frameB);

    // Canvas (South)
    const artTexB = this.textures.loadRetroTexture('Rock 3.png', 1, 1);
    const canvasMatB = new this.THREE.MeshStandardMaterial({
      map: artTexB,
      color: new this.THREE.Color('#00e5ff'), // high cyan console vector
      emissive: new this.THREE.Color('#006064'),
      emissiveIntensity: 0.55,
      roughness: 0.25
    });
    const canvasB = new this.THREE.Mesh(new this.THREE.BoxGeometry(1.4, 1.0, 0.02), canvasMatB);
    canvasB.position.set(0, 1.5, -0.25);
    partition.add(canvasB);

    partition.traverse(node => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
      }
    });

    return partition;
  }

  /**
   * Animated frame updates for elevators and flickering bulbs.
   * @param {number} delta - Frame delta time in seconds
   * @param {Object} playerPos - Player's active absolute position
   */
  updateEntities(delta, playerPos, enableFlickers = true) {
    // 1. Process Flickering ceiling panels
    if (enableFlickers) {
      this.flickeringLights.forEach(light => {
        light.timer -= delta;
        if (light.timer <= 0) {
          // Randomized flicker timings
          const roll = Math.random();
          if (roll > 0.95) {
            light.timer = 1.0 + Math.random() * 3.0; // long steady period
            light.pointLight.intensity = light.baseIntensity;
            light.mesh.material.emissiveIntensity = light.baseEmissive;
          } else {
            // Instant rapid sputter
            light.timer = 0.05 + Math.random() * 0.15;
            const state = Math.random() > 0.4;
            light.pointLight.intensity = state ? light.baseIntensity : 0.1;
            light.mesh.material.emissiveIntensity = state ? light.baseEmissive : 0.1;
 
            // Trigger corresponding sound if the player is very close
            const dx = playerPos.x - (light.pointLight.parent.position.x);
            const dz = playerPos.z - (light.pointLight.parent.position.z);
            const distSq = dx * dx + dz * dz;
            if (distSq < 225 && Math.random() > 0.6) { // inside 15 meters
              const severity = 1.0 - (Math.sqrt(distSq) / 15);
              this.audio.playFlickerSound(severity * 0.8);
            }
          }
        }
      });
    }
 
    // 2. Process Elevator door loops
    this.elevators.forEach(el => {
      // Calculate distance from player
      const dx = playerPos.x - el.cx;
      const dz = playerPos.z - (el.cz - 8.0); // local door offset Z is -8.0
      const dist = Math.sqrt(dx * dx + dz * dz);
 
      // State machine logic
      if (el.state === 'closed') {
        if (dist < 4.0) { // player approached elevator
          el.state = 'opening';
          el.timer = 0;
          
          // Trigger ding sound once when doors unlock and open
          if (!el.soundDinged) {
            this.audio.playElevatorDing();
            el.soundDinged = true;
          }
        }
      } 
      else if (el.state === 'opening') {
        el.slideOffset += delta * 1.5; // open speed
        if (el.slideOffset >= 0.85) {
          el.slideOffset = 0.85;
          el.state = 'open';
          el.timer = 5.0; // stay open for 5 seconds
        }
        // Apply local X offset (doors slide outwards)
        el.doorL.position.x = -0.475 - el.slideOffset;
        el.doorR.position.x = 0.475 + el.slideOffset;
      } 
      else if (el.state === 'open') {
        el.timer -= delta;
        if (el.timer <= 0 && dist > 4.5) { // player walked away, safe to close
          el.state = 'closing';
        }
      } 
      else if (el.state === 'closing') {
        el.slideOffset -= delta * 1.2;
        if (el.slideOffset <= 0) {
          el.slideOffset = 0;
          el.state = 'closed';
          el.soundDinged = false; // reset chime trigger
        }
        el.doorL.position.x = -0.475 - el.slideOffset;
        el.doorR.position.x = 0.475 + el.slideOffset;
      }
    });

    // 3. Process Floor Chevrons running light sequential pulse waves
    this.arrowClock += delta * 7.5; // pulse speed
    this.animatedChevrons.forEach(chev => {
      // Calculate running wave offset using the clock and index
      const intensity = chev.baseEmissive * (0.15 + 0.85 * Math.max(0, Math.sin(this.arrowClock - chev.index * 1.5)));
      chev.mesh.material.emissiveIntensity = intensity;
    });
  }
}
